import { LogInstanceToken } from '@evorait/pace-logging';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { TimeModelOptions } from '../../models/options/time-model-options';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { NumberUtilities } from '../../utilities/number-utilities';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { DropdownModel } from './dropdown.model';
import {
    ODataTimeRecordingEntryOutModel,
    TIMERECORDING_DESTINATION_CODES,
    TIMERECORDING_MEANS_OF_TRANSPORT_CODES,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from './time-recording-entry.model';

export class TimeRecordingLimitedEntryModel extends TimeRecordingEntryModel {
    private readonly configService: ConfigInterface;
    public typeOfWorkCode: TIMERECORDING_TYPE_OF_WORK_CODES | undefined;
    public typeOfWorkDescription: string;
    public billableModel: DropdownModel<TextModel>;
    public isDurationUnchanged: boolean;
    public destination: DropdownModel<TextModel>;
    public meansOfTransport: DropdownModel<TextModel>;
    private readonly enableStandbyTravel: boolean;

    public constructor(
        startDateTime: moment.Moment,
        endDateTime: moment.Moment,
        typeOfWork: TIMERECORDING_TYPE_OF_WORK_CODES | undefined,
        protected billableCode: ConfigConstants.BILLABLE_CODE,
        isDurationUnchanged: boolean,
        private meansOfTransportCode: TIMERECORDING_MEANS_OF_TRANSPORT_CODES,
        private timeRecordingBillableCodes: ValueHelperModel[],
        private timeRecordingTaskTypeInfoCodes: ValueHelperModel[],
        private timeRecordingMeansOfTransportCodes: ValueHelperModel[],
    ) {
        super('TimeRecordingLimitedEntryModel');

        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const translate = injector.get(TranslateService);
        this.configService = injector.get(ConfigInstanceToken);
        const log = injector.get(LogInstanceToken);

        this.enableStandbyTravel = this.configService.getEnableStandbyTravel();
        this.id = moment().valueOf() + NumberUtilities.getRandomNumberBetweenMinAndMax();

        // startDate and startTime
        const start = startDateTime.clone();
        this.startDateModel = <DateModel>this.set(new DateModel('StartDay', start));
        this.startTimeModel = <TimeModel>this.set(new TimeModel('StartTime', start));

        // to avoid a desync for the startDateTime, we will store the current reference
        const currentStartDateTime = this.startDateTime;
        const tomorrowEndOfDay = moment().add(1, 'day').endOf('day');
        this.startDateModel.options = {
            ...this.startDateModel.options,
            maxValue: tomorrowEndOfDay,
        };

        this.startTimeModel.options = {
            ...this.startTimeModel.options,
            maxValue: tomorrowEndOfDay,
        };
        // Due to new created references for both models, we will now restore the previous startDateTime
        this.startTimeModel.value = currentStartDateTime;
        this.startDateModel.value = currentStartDateTime;

        this.isDurationUnchanged = isDurationUnchanged;

        // must happen before the "Duration" initializtaion
        this.typeOfWorkCode = typeOfWork;

        // we want the endTimeModel to be empty when the user never touched the input yet
        const clonedEndDateTime = endDateTime.clone();
        const endTimeToBeSet =
            isDurationUnchanged && typeOfWork === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
                ? moment.invalid()
                : clonedEndDateTime;

        this.endDateModel = <DateModel>this.set(new DateModel('EndDay', clonedEndDateTime));
        this.endTimeModel = <TimeModel>(
            this.set(
                new TimeModel(
                    'EndTime',
                    endTimeToBeSet,
                    new TimeModelOptions({ readonly: !this.endDateModel.value.isValid() }),
                ),
            )
        );

        // we already have the code, but we need the description text to show it on the ui
        const valueHelperModelArray = timeRecordingTaskTypeInfoCodes.filter((vh: ValueHelperModel) => {
            return vh.code === <string>this.typeOfWorkCode;
        });
        if (valueHelperModelArray.length === 0) {
            log.error(
                `TimeRecordingLimitedEntryModel: error: ${this.typeOfWorkCode} not included in timeRecordingTaskTypeInfoCodes`,
            );
        }
        if (valueHelperModelArray.length === 1) {
            this.typeOfWorkDescription = valueHelperModelArray[0].descText;
        }

        if (this.enableStandbyTravel && this.typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            this.typeOfWorkDescription = <string>translate.instant('CONFIRMATION_TIMERECORDING_TRAVEL_BACK_STANDBY');
        }

        // Billable
        const billableEntries = this.timeRecordingBillableCodes.map((vh: ValueHelperModel) => {
            return new DisplayDecorator<TextModel>(vh.descText, new TextModel('BillableEntry', vh.code));
        });
        this.billableModel = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('Billable', billableEntries))
        );
        this.billableModel.selectByPredicate((billableEntry) => {
            return billableEntry.model.value === billableCode;
        });

        // Means Of Transportation

        if (this.configService.getDisplayMeansOfTransport()) {
            const transportationEntries = timeRecordingMeansOfTransportCodes.map((vh: ValueHelperModel) => {
                return new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.code, vh.code));
            });
            this.meansOfTransport = new DropdownModel<TextModel>('MeansOfTransport', transportationEntries);
            modelOptionsLoader.load(this.meansOfTransport, 'TimeRecording', 'MeansOfTransport');

            this.meansOfTransport.selectByPredicate((transportEntry) => {
                return transportEntry.model.value === meansOfTransportCode;
            });
        }
    }

    public setIsDurationUnchanged(isDurationUnchanged: boolean): void {
        this.isDurationUnchanged = isDurationUnchanged;
    }

    public getTypeOfWorkCode(): TIMERECORDING_TYPE_OF_WORK_CODES | undefined {
        return this.typeOfWorkCode;
    }

    public clone(): TimeRecordingLimitedEntryModel {
        const instance = new TimeRecordingLimitedEntryModel(
            this.startDateTime,
            this.endDateTime,
            this.typeOfWorkCode,
            this.billableModel.selectedEntry
                ? <ConfigConstants.BILLABLE_CODE>this.billableModel.selectedEntry.value
                : this.billableCode,
            this.isDurationUnchanged,
            this.meansOfTransport && this.meansOfTransport.selectedEntry
                ? <TIMERECORDING_MEANS_OF_TRANSPORT_CODES>this.meansOfTransport.selectedEntry.value
                : this.meansOfTransportCode,
            this.timeRecordingBillableCodes,
            this.timeRecordingTaskTypeInfoCodes,
            this.timeRecordingMeansOfTransportCodes,
        );
        return instance;
    }

    public getOutModel(): ODataTimeRecordingEntryOutModel {
        const outModel = super.getOutModel();

        // The TypeOfWorkCode which is just shown as label still needs to be added to the outmodel
        // For korea, we have TRBS instead of TRBK.
        const typeOfWork = new TextModel('WorkCode', this.typeOfWorkCode);
        if (this.typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK && this.enableStandbyTravel) {
            typeOfWork.value = TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY;
        }

        _.merge(outModel, typeOfWork.getOutModel());

        return outModel;
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): unknown {
        const jsonModel = {
            name: this.name,
            startDateTime: this.startDateModel.value,
            endDateTime: this.endTimeModel.value,
            typeOfWork: this.typeOfWorkCode,
            billableCode: this.billableModel.selectedEntry ? this.billableModel.selectedEntry.value : undefined,
            isDurationUnchanged: this.isDurationUnchanged,
            meansOfTransport:
                this.meansOfTransport && this.meansOfTransport.selectedEntry
                    ? this.meansOfTransport.selectedEntry.value
                    : undefined,
            destination:
                this.destination && this.destination.selectedEntry ? this.destination.selectedEntry.value : undefined,
        };

        if (typeof this.billableModel.selectedEntry === 'undefined' && this.enableStandbyTravel) {
            jsonModel.billableCode = this.billableCode;
        }

        return jsonModel;
    }
}

export class TimeRecordingLimitedEntryModelFromJson extends TimeRecordingLimitedEntryModel {
    public constructor(
        data: JsonTimeRecordingLimitedEntryModel,
        timeRecordingBillableCodes: ValueHelperModel[],
        timeRecordingTaskTypeInfoCodes: ValueHelperModel[],
        timeRecordingMeansOfTransportationCodes: ValueHelperModel[],
    ) {
        // Migration logic from 4.11.1 where end date is empty and new version we need only end date but end time should be empty
        const endDateTime = !moment(data.endDateTime).isValid() ? moment(data.startDateTime) : moment(data.endDateTime);
        super(
            moment(data.startDateTime),
            endDateTime,
            data.typeOfWork,
            data.billableCode,
            data.isDurationUnchanged,
            data.meansOfTransport,
            timeRecordingBillableCodes,
            timeRecordingTaskTypeInfoCodes,
            timeRecordingMeansOfTransportationCodes,
        );
    }
}

export interface JsonTimeRecordingLimitedEntryModel {
    name: string;
    startDateTime: string;
    endDateTime: string;
    typeOfWork: TIMERECORDING_TYPE_OF_WORK_CODES;
    billableCode: ConfigConstants.BILLABLE_CODE;
    isDurationUnchanged: boolean;
    meansOfTransport: TIMERECORDING_MEANS_OF_TRANSPORT_CODES;
    destination: TIMERECORDING_DESTINATION_CODES;
}

export function isTimeRecordingLimitedEntryModel(
    entry: TimeRecordingEntryModel,
): entry is TimeRecordingLimitedEntryModel {
    return entry.name === 'TimeRecordingLimitedEntryModel';
}
